// Image helpers: use small thumbnails instead of full-size photos, and shrink photos before upload.
//
// Thumbnails are made in the browser when a photo is uploaded (uploadThumbnails), and once for older
// photos by scripts/make-thumbnails.mjs:
//   events/{id}/photo.jpg  ->  events/{id}/thumbs/photo_800x800.webp
// Until a thumbnail exists, <SmartImage> falls back to the original, so nothing breaks.

import React, { useState } from 'react';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';

export const THUMB_SIZES = [200, 800, 1600];

// Thumbnails that turned out not to exist, so we don't ask for them again this session
const missing = new Set();

// Firebase Storage download URL -> URL of the resized version (or null if not a Storage URL)
export function thumbUrl(url, size) {
  if (!url) return null;
  const match = url.match(/^(https?:\/\/[^/]+\/v0\/b\/[^/]+\/o\/)([^?]+)/);
  if (!match) return null;
  const path = decodeURIComponent(match[2]);
  const slash = path.lastIndexOf('/');
  const dir = path.slice(0, slash);
  const file = path.slice(slash + 1);
  if (dir.endsWith('/thumbs')) return url; // already a thumbnail
  const base = file.includes('.') ? file.slice(0, file.lastIndexOf('.')) : file;
  return `${match[1]}${encodeURIComponent(`${dir}/thumbs/${base}_${size}x${size}.webp`)}?alt=media`;
}

// <img> that loads the right-sized thumbnail and falls back to the original if it's missing
export function SmartImage({ src, size = 800, alt = '', ...props }) {
  const thumb = thumbUrl(src, size);
  const [failedThumb, setFailedThumb] = useState(null);
  const useOriginal = !thumb || missing.has(thumb) || failedThumb === thumb;
  return (
    <img
      src={useOriginal ? src : thumb}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (!useOriginal) {
          missing.add(thumb);
          setFailedThumb(thumb);
        }
      }}
      {...props}
    />
  );
}

// Shrinks a photo before upload (longest side maxSize px). Small files are uploaded as they are.
export async function shrinkImage(file, maxSize = 2560, quality = 0.86) {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file; // e.g. HEIC in a browser that can't decode it: upload the original
  }
}

// Storage path of a thumbnail: events/{id}/photo.jpg -> events/{id}/thumbs/photo_800x800.webp
// (same naming as thumbUrl() above and scripts/make-thumbnails.mjs)
export function thumbPath(path, size) {
  const slash = path.lastIndexOf('/');
  const file = path.slice(slash + 1);
  const base = file.includes('.') ? file.slice(0, file.lastIndexOf('.')) : file;
  return `${path.slice(0, slash)}/thumbs/${base}_${size}x${size}.webp`;
}

// Resized copies of a photo for the given sizes (fits inside size x size, never enlarged)
export async function makeThumbnails(file, sizes = THUMB_SIZES) {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const result = [];
  for (const size of sizes) {
    const scale = Math.min(1, size / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.82));
    if (blob) result.push({ size, blob });
  }
  return result;
}

// Uploads thumbnails next to an uploaded photo. Failures are only logged: the app falls back to the original.
export async function uploadThumbnails(storage, path, file, sizes = THUMB_SIZES) {
  try {
    const thumbs = await makeThumbnails(file, sizes);
    await Promise.all(thumbs.map(({ size, blob }) => uploadBytes(ref(storage, thumbPath(path, size)), blob, {
      contentType: blob.type || 'image/webp',
      cacheControl: 'public, max-age=31536000',
    })));
  } catch (error) {
    console.error('Could not create thumbnails: ', error);
  }
}

// Deletes the thumbnails of a photo (ignores the ones that don't exist)
export async function deleteThumbnails(storage, path, sizes = THUMB_SIZES) {
  await Promise.all(sizes.map(size => deleteObject(ref(storage, thumbPath(path, size))).catch(() => {})));
}
